import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, Subject, EMPTY } from 'rxjs';
import { catchError, finalize, share, switchMap, tap } from 'rxjs/operators';
import { OauthService } from '@monsido/ng2/external/oauth/oauth.service';
import { tokenErrorMessages } from '../token-issue-messages.constant';
import { MonEventService } from '@monsido/ng2/services/mon-event/mon-event.service';


@Injectable({
    providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {

    private tokenRefreshed$: Subject<void> | null = new Subject<void>();
    private tokenRefreshInProgress$: Observable<unknown> | null = null;
    private isTokenInvalid = false;

    constructor (
        private oauthService: OauthService,
        private monEventService: MonEventService,
    ) {
        this.monEventService.addListener('scheduledRefreshTokenRequest', () => {
            if (!this.tokenRefreshInProgress$) {
                this.tokenRefreshInProgress$ = this.startRefreshToken();
                this.tokenRefreshInProgress$.subscribe();
            }
        });
    }

    intercept (request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (this.isTokenInvalid && !this.isOauthRequest(request.url)) {
            return this.replayRequestOnTokenRefreshed(request, next);
        }

        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse) => {
                if (this.issueWithAccessToken401(error)) {
                    if (this.isTokenInvalid) {
                        return this.replayRequestOnTokenRefreshed(request, next);
                    }

                    this.isTokenInvalid = true;
                    if (!this.tokenRefreshInProgress$) {
                        this.tokenRefreshInProgress$ = this.startRefreshToken();
                    }
                    return this.tokenRefreshInProgress$.pipe(
                        switchMap(() => next.handle(
                            this.oauthService.updateRequestToken(request),
                        )),
                    );
                }

                if (this.tokenRefreshingError(request)) {
                    this.monEventService.run('force logout');
                }
                return throwError(error);
            }),
        );
    }

    private startRefreshToken () {
        return this.oauthService.refreshAccessToken()
            .pipe(
                tap(() => this.setTokenRefreshed()),
                catchError(() => {
                    this.setTokenRefreshed();
                    // Force logout if refreshing token is failed
                    this.monEventService.run('force logout');
                    return EMPTY;
                }),
                finalize(() => {
                    this.tokenRefreshInProgress$ = null;
                }),
                share(),
            );
    }

    private replayRequestOnTokenRefreshed (request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        this.tokenRefreshed$ = new Subject<void>();
        return this.tokenRefreshed$.pipe(
            switchMap(() => {
                return next.handle(
                    this.oauthService.updateRequestToken(request),
                );
            }),
        );
    }

    private isOauthRequest (url: string): boolean {
        return url.endsWith('/oauth/token');
    }

    private setTokenRefreshed (): void {
        this.isTokenInvalid = false;
        this.tokenRefreshed$?.next();
        this.tokenRefreshed$?.complete();
    }

    private issueWithAccessToken401 (error: HttpErrorResponse): boolean {
        return Boolean(error && error.status === 401 && error.error && tokenErrorMessages.includes(error.error.message));
    }

    private tokenRefreshingError (request: HttpRequest<unknown>): boolean {
        return request.params.get('grant_type') === 'refresh_token';
    }

}
